// Copyright ©️ 2026 eVolve MEP, LLC

import { formatDate, type FacilityId, type TaskData, type TaskTimeEntry } from '@evolvemep/api-ts';
import type { ColDef } from 'ag-grid-enterprise';

import { CellRenderer } from 'components/Mantine/CellRenderer';
import { hoursColDef } from 'components/Mantine/commonColumnDefs';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import { lockedColDef } from 'helpers/ag-grid/baseColumnDef';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import { WorkCellTaskLink } from 'modules/Shop/Tasks/WorkCellTaskLink';
import { TimeEntryMenu } from 'modules/Shop/TimeTracking/TimeEntryMenu';

export const getTimeTrackingColDef = ({
  facilityId,
  onDeleteEntry,
}: {
  facilityId: FacilityId;
  onDeleteEntry: (entry: TaskTimeEntry) => void;
}): ColDef<TaskTimeEntry>[] => [
  {
    field: 'workDate',
    headerName: 'Date',
    valueFormatter: (p) => formatDate(p.value, 'MM/dd/yyyy', ''),
  },
  {
    colId: 'userName',
    field: 'user.nameOrEmail',
    flex: 1,
    minWidth: 200,
    valueFormatter: ({ data }) => validateUserNameOrEmail(data?.user),
    cellClass: ({ data }) => (data?.user.isDeleted ? 'disabled-row' : undefined),
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'taskName',
    minWidth: 180,
    flex: 1,
    cellRenderer: CellRenderer<TaskTimeEntry>(({ data }) => <WorkCellTaskLink task={data} from="Time Tracking" />),
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'projectName',
    headerName: 'Project',
  },
  {
    field: 'workOrderIdentifier',
    headerName: 'Work Order Id',
    cellRenderer: CellRenderer<TaskData>(({ value, data }) => (
      <EvolveLink to={`/work-orders/${data.workOrderId}`} from="Task Data">
        {value}
      </EvolveLink>
    )),
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'workOrderName',
    headerName: 'Work Order Name',
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'totalSeconds',
    headerName: 'Hours',
    width: 100,
    ...hoursColDef(),
  },
  {
    ...lockedColDef('right'),
    width: 48,
    cellRenderer: CellRenderer<TaskTimeEntry>(({ data, node, api }) => (
      <TimeEntryMenu
        entry={data}
        facilityId={facilityId}
        onDeleteEntry={onDeleteEntry}
        onEntryUpdated={(entry) => {
          // `onEntryUpdated` is also called when a new entry is created via duplication
          if (entry.taskTimeEntryId === data.taskTimeEntryId) {
            node.updateData(entry);
          }
          refreshGrid(api);
        }}
      />
    )),
  },
];
